/**
 * @fileoverview useAssignmentForm Custom Hook
 * @author MCHD Room Scheduler Team
 * @created 2025-10-08 23:55:00
 * 
 * Custom hook for assignment form state management and validation.
 * Enforces hospital-grade validation and 7 Hard Rules compliance.
 */

import { useState, useMemo, useCallback } from 'react';
import { startOfDay, isBefore } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { notifications } from '@mantine/notifications';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface Doctor {
  id: string;
  name: string;
  email: string;
  specialty?: string;
  department?: string;
  role?: string;
}

interface SessionTime {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  isActive: boolean;
}

interface FilterOptions {
  buildings: Array<{ value: string; label: string }>;
  floors: Array<{ value: string; label: string }>;
  rooms: Array<{ value: string; label: string }>;
  sessionTimes: Array<{ value: string; label: string }>;
  users: Array<{ value: string; label: string }>;
}

interface Assignment {
  id: string;
  title: string;
  description?: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';
  date: string;
  room: Room;
  user?: Doctor;
  users?: Doctor;
  doctor?: Doctor;
  sessionTime?: SessionTime;
  session_times?: SessionTime;
}

interface UseAssignmentFormProps {
  room?: Room | undefined;
  sessionTime?: SessionTime | undefined;
  date?: Date | undefined;
  doctors?: Doctor[] | undefined;
  filterOptions?: FilterOptions | undefined;
  contextData?: {
    selectedBuilding?: string | null;
    selectedFloor?: string | null;
    selectedRooms?: string[];
    dateRange?: [Date, Date];
  } | undefined;
  // ENHANCED: 09-10-2025 01:30:00 - Edit mode support
  selectedAssignment?: Assignment | null;
}

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Custom hook for assignment form management
 * 
 * Features:
 * - Hierarchical selection state management
 * - Timezone-aware date handling
 * - Doctor filtering and validation
 * - Form validation with 7 Hard Rules compliance
 * - Production-ready error handling
 * 
 * @param props - Hook configuration props
 * @returns Assignment form state and handlers
 */
export const useAssignmentForm = ({
  room,
  sessionTime,
  date,
  doctors = [],
  filterOptions,
  contextData,
  selectedAssignment
}: UseAssignmentFormProps) => {
  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================
  
  // Helper to get user data from assignment
  const getUserData = () => {
    if (!selectedAssignment) return null;
    // Try multiple sources: doctor, users, user (in order of preference based on API response)
    return selectedAssignment.doctor || selectedAssignment.users || selectedAssignment.user || null;
  };

  // Form state - Initialize with selectedAssignment data if editing
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>(() => {
    if (selectedAssignment) {
      const currentUser = getUserData();
      return currentUser?.id || selectedAssignment.userId || '';
    }
    return '';
  });
  const [note, setNote] = useState<string>(() => {
    return selectedAssignment?.description || '';
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  
  // Hierarchical selection state - Initialize with selectedAssignment data if editing
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>(() => {
    // If editing existing assignment, use assignment's building data
    if (selectedAssignment?.room && 'floor' in selectedAssignment.room) {
      const roomWithFloor = selectedAssignment.room as any;
      if (roomWithFloor.floor?.building?.id) {
        return roomWithFloor.floor.building.id;
      }
    }
    // Otherwise use contextData or default values
    return room?.id ? '' : contextData?.selectedBuilding || '';
  });
  const [selectedFloorId, setSelectedFloorId] = useState<string>(() => {
    // If editing existing assignment, use assignment's floor data
    if (selectedAssignment?.room && 'floor' in selectedAssignment.room) {
      const roomWithFloor = selectedAssignment.room as any;
      if (roomWithFloor.floor?.id) {
        return roomWithFloor.floor.id;
      }
    }
    // Otherwise use contextData or default values
    return room?.id ? '' : contextData?.selectedFloor || '';
  });
  const [selectedRoomId, setSelectedRoomId] = useState<string>(() => {
    return selectedAssignment?.roomId || room?.id || '';
  });
  const [selectedSessionTimeId, setSelectedSessionTimeId] = useState<string>(() => {
    return selectedAssignment?.sessionTemplateId || sessionTime?.id || '';
  });
  
  // Timezone-aware date handling
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const today = startOfDay(new Date());
    
    // If editing existing assignment, use assignment date
    if (selectedAssignment?.date) {
      return new Date(selectedAssignment.date);
    }
    
    // If date prop is provided (from grid cell click), use it if it's not in the past
    if (date) {
      const providedDate = startOfDay(date);
      
      // Only use provided date if it's today or in the future
      if (!isBefore(providedDate, today)) {
        return providedDate;
      }
    }
    
    // Default to today for topbar "Create Assignment" button
    return today;
  });

  // =============================================================================
  // HIERARCHICAL FILTERING LOGIC
  // =============================================================================
  
  /**
   * Filter floors by selected building
   */
  const availableFloors = useMemo(() => {
    const options = [];
    
    // Add floors from API if available
    if (filterOptions?.floors && filterOptions.floors.length > 0) {
      options.push(...filterOptions.floors);
    }
    
    // If editing and no floors from API, add assignment's floor as fallback
    if (selectedAssignment?.room && 'floor' in selectedAssignment.room && options.length === 0) {
      const roomWithFloor = selectedAssignment.room as any;
      if (roomWithFloor.floor) {
        options.push({
          value: roomWithFloor.floor.id,
          label: roomWithFloor.floor.name,
        });
      }
    }
    
    return options;
  }, [selectedBuildingId, filterOptions?.floors, selectedAssignment]);
  
  /**
   * Filter rooms by selected floor
   */
  const availableRooms = useMemo(() => {
    const options = [];
    
    // Add rooms from API if available
    if (filterOptions?.rooms && filterOptions.rooms.length > 0) {
      options.push(...filterOptions.rooms);
    }
    
    // If editing and no rooms from API, add assignment's room as fallback
    if (selectedAssignment?.room && options.length === 0) {
      const currentRoom = selectedAssignment.room;
      options.push({
        value: currentRoom.id,
        label: `${currentRoom.number} - ${currentRoom.name}`,
      });
    }
    
    return options;
  }, [selectedFloorId, filterOptions?.rooms, selectedAssignment]);
  
  /**
   * Filter session times by selected room
   * CRITICAL: 09-10-2025 00:30:00 - Only show sessions that belong to the selected room
   * Prevents cross-room session assignment (7 Hard Rules #6)
   */
  const availableSessionTimes = useMemo(() => {
    const options = [];
    
    // Add session times from API if available
    if (filterOptions?.sessionTimes && filterOptions.sessionTimes.length > 0) {
      const filteredSessions = filterOptions.sessionTimes.filter((st: any) => st.roomId === selectedRoomId);
      options.push(...filteredSessions);
    }
    
    // If editing and no session times from API, add assignment's session time as fallback
    if (selectedAssignment && options.length === 0) {
      const currentSessionTime = selectedAssignment.sessionTime || selectedAssignment.session_times;
      if (currentSessionTime) {
        options.push({
          value: currentSessionTime.id,
          label: `${currentSessionTime.name} (${currentSessionTime.startTime} - ${currentSessionTime.endTime})`,
        });
      }
    }
    
    return options;
  }, [selectedRoomId, filterOptions?.sessionTimes, selectedAssignment]);

  // =============================================================================
  // DOCTOR FILTERING LOGIC
  // =============================================================================
  
  /**
   * Debounced search value for performance
   */
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);
  
  // Update debounced search value
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchValue]);

  /**
   * Filter doctors based on search and role validation
   */
  const filteredDoctors = useMemo(() => {
    try {
      // If editing and no doctors from API, include the current doctor from assignment
      if (selectedAssignment && (!doctors || !Array.isArray(doctors) || doctors.length === 0)) {
        const currentDoctor = getUserData();
        if (currentDoctor) {
          return [currentDoctor];
        }
        return [];
      }
      
      // Comprehensive null checks to prevent crashes
      if (!doctors || !Array.isArray(doctors) || doctors.length === 0) {
        return [];
      }
      
      // Filter out any invalid doctor objects
      const validDoctors = doctors.filter(doctor => {
        return doctor && 
          typeof doctor === 'object' && 
          doctor.id && 
          doctor.name && 
          doctor.role === 'DOCTOR';  // Only include doctors, not coordinators/admins
      });
      
      // If editing, ensure the current doctor is included even if not in API response
      if (selectedAssignment) {
        const currentDoctor = getUserData();
        if (currentDoctor && !validDoctors.find(d => d.id === currentDoctor.id)) {
          validDoctors.unshift(currentDoctor);
        }
      }
      
      if (validDoctors.length === 0) {
        return [];
      }
      
      if (!debouncedSearchValue || !debouncedSearchValue.trim()) {
        return validDoctors.slice(0, 10); // Show first 10 doctors when no search
      }
      
      return validDoctors.filter(doctor =>
        doctor?.name?.toLowerCase().includes(debouncedSearchValue.toLowerCase()) ||
        doctor?.specialty?.toLowerCase().includes(debouncedSearchValue.toLowerCase())
      );
    } catch (error) {
      // Silent error handling for production
      return [];
    }
  }, [doctors, debouncedSearchValue]);

  // =============================================================================
  // VALIDATION LOGIC
  // =============================================================================
  
  /**
   * Validate form inputs before submission
   * Enforces 7 Hard Rules and hospital-grade validation
   */
  const validateFormInputs = useCallback((): ValidationResult => {
    if (!selectedDoctorId) {
      return { isValid: false, error: 'Please select a doctor' };
    }

    const finalRoom = room || (selectedRoomId ? { id: selectedRoomId } : null);
    const finalSessionTime = sessionTime || (selectedSessionTimeId ? { id: selectedSessionTimeId } : null);
    const finalDate = selectedDate || date;

    if (!finalRoom || !finalSessionTime || !finalDate) {
      return { isValid: false, error: 'Please complete all required selections: building, floor, room, session time, and date' };
    }

    // Additional validation for past dates (7 Hard Rules: Past Session Lock)
    if (isBefore(startOfDay(finalDate), startOfDay(new Date()))) {
      return { isValid: false, error: 'Cannot create assignments for past dates. Please select today or a future date.' };
    }

    return { isValid: true };
  }, [selectedDoctorId, room, selectedRoomId, sessionTime, selectedSessionTimeId, selectedDate, date]);

  // =============================================================================
  // DATA CREATION HELPERS
  // =============================================================================
  
  /**
   * Create assignment data with timezone-aware date formatting
   */
  const createAssignmentData = useCallback((finalRoom: any, finalSessionTime: any, finalDate: Date) => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateOnlyString = formatInTimeZone(finalDate, userTimezone, 'yyyy-MM-dd');
    
    return {
      title: `${finalRoom.name || 'Selected Room'} - ${finalSessionTime.name || 'Selected Session'}`,
      ...(note.trim() && { description: note.trim() }),
      roomId: finalRoom.id,
      userId: selectedDoctorId,
      sessionTemplateId: finalSessionTime.id,
      date: dateOnlyString
    };
  }, [note, selectedDoctorId]);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  /**
   * Handle building selection with cascading resets
   */
  const handleBuildingChange = useCallback((value: string) => {
    setSelectedBuildingId(value);
    setSelectedFloorId('');
    setSelectedRoomId('');
    setSelectedSessionTimeId('');
  }, []);

  /**
   * Handle floor selection with cascading resets
   */
  const handleFloorChange = useCallback((value: string) => {
    setSelectedFloorId(value);
    setSelectedRoomId('');
    setSelectedSessionTimeId('');
  }, []);

  /**
   * Handle room selection with cascading resets
   */
  const handleRoomChange = useCallback((value: string) => {
    setSelectedRoomId(value);
    setSelectedSessionTimeId('');
  }, []);

  /**
   * Handle assignment creation success
   */
  const handleAssignmentSuccess = useCallback(() => {
    notifications.show({
      title: 'Success',
      message: 'Doctor assigned successfully.',
      color: 'green'
    });
  }, []);

  /**
   * Handle assignment creation errors with detailed conflict information
   */
  const handleAssignmentError = useCallback((error: any) => {
    let errorMessage = 'Failed to create assignment';
    let conflicts: any[] = [];
    let suggestions: any[] = [];
    
    if (error instanceof Error) {
      errorMessage = error.message;
      conflicts = (error as any).conflicts || [];
      suggestions = (error as any).suggestions || [];
    } else if (error && typeof error === 'object' && 'message' in error) {
      errorMessage = (error as any).message;
      conflicts = (error as any).conflicts || [];
      suggestions = (error as any).suggestions || [];
    }
    
    if (conflicts.length > 0) {
      notifications.show({
        title: 'Assignment Conflicts Detected',
        message: `${conflicts.length} conflict${conflicts.length > 1 ? 's' : ''} found: ${conflicts.map((c: any) => c.message).join(', ')}`,
        color: 'red',
        autoClose: 8000,
        withCloseButton: true
      });
      
      if (suggestions.length > 0) {
        setTimeout(() => {
          notifications.show({
            title: 'Suggested Solutions',
            message: suggestions.map((s: any) => s.message || s.action).join(', '),
            color: 'blue',
            autoClose: 10000
          });
        }, 1000);
      }
    } else {
      notifications.show({
        title: 'Assignment Failed',
        message: errorMessage,
        color: 'red'
      });
    }
  }, []);

  // =============================================================================
  // RETURN HOOK INTERFACE
  // =============================================================================
  
  return {
    // Form state
    selectedDoctorId,
    note,
    submitting,
    searchValue,
    
    // Hierarchical selection state
    selectedBuildingId,
    selectedFloorId,
    selectedRoomId,
    selectedSessionTimeId,
    selectedDate,
    
    // Filtered data
    availableFloors,
    availableRooms,
    availableSessionTimes,
    filteredDoctors,
    
    // State setters
    setSelectedDoctorId,
    setNote,
    setSubmitting,
    setSearchValue,
    setSelectedDate,
    
    // Event handlers
    onBuildingChange: handleBuildingChange,
    onFloorChange: handleFloorChange,
    onRoomChange: handleRoomChange,
    onSessionTimeChange: setSelectedSessionTimeId,
    onDateChange: setSelectedDate,
    onNoteChange: setNote,
    onDoctorChange: setSelectedDoctorId,
    
    // Validation and data creation
    validateFormInputs,
    createAssignmentData,
    handleAssignmentSuccess,
    handleAssignmentError,
    
    // Computed values
    canSubmit: selectedDoctorId && selectedDate && !submitting,
    finalRoom: room || (selectedRoomId ? { id: selectedRoomId } : null),
    finalSessionTime: sessionTime || (selectedSessionTimeId ? { id: selectedSessionTimeId } : null),
    finalDate: selectedDate || date
  };
};

export default useAssignmentForm;
